import React, { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useNavigate } from "react-router";
import useCustomStyles from "../../hooks/useCustomStyles";
import { accessCodesStyles } from "../../assets/styles/accessCodesStyles";
import { useSelector, useDispatch } from "react-redux";
import { selectState } from "../../store/slices/themeSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSelect from "../../components/CustomSelectInput";
import CustomInput from "../../components/CustomInput";
import ActiveInActiveButton from "../clients/components/ActiveInActiveButton";
import { AppDispatch, RootState } from "../../store/store";
import { createBusiness, uploadBusinessTheme } from "../../store/actions/businessActions";
import {
  clearErrors,
  clearSuccessFlags,
  clearUploadFlags,
} from "../../store/slices/businessSlice";
import { toast } from "react-toastify";

const WhiteLabelingForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useSelector(selectState);
  const { 
    creating, 
    createSuccess, 
    createError,
    uploading,
    uploadSuccess,
    uploadError,
    currentBusiness,
  } = useSelector((state: RootState) => state.business_slice);

  const classes = useCustomStyles(accessCodesStyles, theme);

  // Store the created business code for theme upload
  const [createdBusinessCode, setCreatedBusinessCode] = useState<string | null>(null);

  // Handle create success
  useEffect(() => {
    if (createSuccess) {
      toast.success("Business created successfully!");
      dispatch(clearSuccessFlags());
      
    }
  }, [createSuccess, dispatch]);

  useEffect(() => {
    if(currentBusiness) {
      navigate(`/businesses/${currentBusiness.id}`);
      }
  }, [currentBusiness]);  

  // Handle create error
  useEffect(() => {
    if (createError) {
      toast.error(createError);
      dispatch(clearErrors());
    }
  }, [createError, dispatch]);

  // Handle upload success
  useEffect(() => {
    if (uploadSuccess) {
      toast.success("Theme uploaded successfully!");
      formik.resetForm();
      setCreatedBusinessCode(null);
      dispatch(clearUploadFlags());
    }
  }, [uploadSuccess, dispatch]);

  // Handle upload error
  useEffect(() => {
    if (uploadError) {
      toast.error(uploadError);
      dispatch(clearUploadFlags());
    }
  }, [uploadError, dispatch]);

  // Generate business code from name (same logic as backend)
  const generateBusinessCode = (name: string): string => {
    return name.trim().toUpperCase().replace(/\s+/g, "_");
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    type: Yup.string().required("Please select a type"),
    enableAdvisors: Yup.boolean(),
    needClientId: Yup.boolean(),
    needAdminDashboard: Yup.boolean(),
    masterAdmin: Yup.string().when("needAdminDashboard", {
      is: true,
      then: () =>
        Yup.string()
          .email("Invalid email format")
          .required("Master admin is required"),
      otherwise: () => Yup.string(),
    }),
    normalAdmins: Yup.array().of(Yup.string().email("Invalid email format")),
    jsonTheme: Yup.string().test(
      "is-valid-json",
      "Invalid JSON format",
      (value) => {
        if (!value || value.trim() === "") return true; // Allow empty
        try {
          JSON.parse(value);
          return true;
        } catch {
          return false;
        }
      }
    ),
  });

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      name: "",
      type: "",
      enableAdvisors: false,
      needClientId: false,
      needAdminDashboard: false,
      masterAdmin: "",
      normalAdmins: [] as string[],
      jsonTheme: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const admins: Array<{ email: string; roles: string[] }> = [];

      if (values.needAdminDashboard) {
        if (values.masterAdmin) {
          admins.push({
            email: values.masterAdmin,
            roles: ["master", "administrator"],
          });
        }

        if (values.normalAdmins && values.normalAdmins.length > 0) {
          values.normalAdmins
            .filter((email) => email.trim() !== "")
            .forEach((email) => {
              admins.push({
                email: email.trim(),
                roles: ["administrator"],
              });
            });
        }
      }

      // Store the business code for theme upload
      const businessCode = generateBusinessCode(values.name);
      setCreatedBusinessCode(businessCode);

      dispatch(
        createBusiness({
          name: values.name,
          type: values.type.toLowerCase(),
          enable_advisors: values.enableAdvisors,
          need_client_id: values.needClientId,
          need_admin_dashboard: values.needAdminDashboard,
          admins: values.needAdminDashboard ? admins : undefined,
        })
      );
    },
  });

  // Handle theme upload
  const handleUploadTheme = () => {
    const { jsonTheme, name } = formik.values;

    if (!jsonTheme || jsonTheme.trim() === "") {
      toast.error("Please paste a JSON theme first");
      return;
    }

    // Validate JSON
    try {
      JSON.parse(jsonTheme);
    } catch {
      toast.error("Invalid JSON format");
      return;
    }

    if (!name || name.trim() === "") {
      toast.error("Please enter a business name first");
      return;
    }

    const clientCode = generateBusinessCode(name);

    dispatch(
      uploadBusinessTheme({
        clientCode,
        jsonTheme,
      })
    );
  };

  // Check if type is "Partner"
  const isPartner = formik.values.type.toLowerCase() === "partner";

  // Reset toggle values when type changes to non-partner
  useEffect(() => {
    if (!isPartner) {
      formik.setFieldValue("enableAdvisors", false);
      formik.setFieldValue("needClientId", false);
      formik.setFieldValue("needAdminDashboard", false);
      formik.setFieldValue("masterAdmin", "");
      formik.setFieldValue("normalAdmins", []);
      formik.setFieldValue("jsonTheme", "");
    }
  }, [formik.values.type]);

  // Preview of generated business code
  const previewCode = formik.values.name
    ? generateBusinessCode(formik.values.name)
    : "";

  const styles: Record<string, React.CSSProperties> = {
    formContainer: {
      width: "50%",
    },
    adminSectionContainer: {
      flexDirection: "row",
      display: "flex",
      alignItems: "center",
      alignContent: "center",
      justifyContent: "space-between",
    },
    addButton: {
      height: 30,
      width: 30,
      borderRadius: 4,
      marginLeft: 10,
      backgroundColor: "#4CAF50",
      color: "#fff",
      border: "none",
      cursor: "pointer",
      fontSize: 18,
    },
    removeButton: {
      height: 30,
      width: 30,
      borderRadius: 4,
      marginLeft: 10,
      backgroundColor: "#f44336",
      color: "#fff",
      border: "none",
      cursor: "pointer",
      fontSize: 18,
    },
    adminInputContainer: {
      display: "flex",
      alignItems: "center",
      marginBottom: 10,
    },
    adminInputWrapper: {
      flex: 1,
    },
    jsonSection: {
      marginTop: 20,
      marginBottom: 20,
    },
    jsonLabel: {
      fontSize: 16,
      fontWeight: "500",
      marginBottom: 8,
      display: "block",
      color: theme?.colorSchema?.colors?.mainText || "#333",
    },
    jsonTextarea: {
      width: "100%",
      minHeight: 200,
      padding: 12,
      borderRadius: 12,
      border: "1px solid #ccc",
      fontFamily: "monospace",
      fontSize: 14,
      resize: "vertical",
    },
    jsonError: {
      color: "#f44336",
      fontSize: 12,
      marginTop: 4,
    },
    codePreview: {
      fontSize: 14,
      color: "#666",
      marginTop: 4,
      fontStyle: "italic",
    },
    uploadButtonContainer: {
      marginTop: 10,
      display: "flex",
      justifyContent: "flex-end",
    },
    uploadButton: {
      padding: "10px 20px",
      backgroundColor: "#2196F3",
      color: "#fff",
      border: "none",
      borderRadius: 8,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: "500",
    },
    uploadButtonDisabled: {
      backgroundColor: "#ccc",
      cursor: "not-allowed",
    },
  };

  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <h2 className={classes.header}>Create Business</h2>
      </div>

      <div style={styles.formContainer}>
        <form onSubmit={formik.handleSubmit}>
          {/* Name Input */}
          <CustomInput
            name="name"
            label="Name"
            type="text"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlure={formik.handleBlur}
            placeholder="Enter business name"
            error={
              formik.errors.name && formik.touched.name
                ? formik.errors.name
                : ""
            }
          />
          {/* Show preview of generated code */}
          {previewCode && (
            <p style={styles.codePreview}>
              Generated Code: <strong>{previewCode}</strong>
            </p>
          )}

          {/* Type Select */}
          <CustomSelect
            name="type"
            label="Type"
            placeholder="Select type"
            value={formik.values.type}
            options={["Partner", "Distributer"]}
            onChange={(e) => formik.setFieldValue("type", e.target.value)}
            error={
              formik.errors.type && formik.touched.type
                ? formik.errors.type
                : ""
            }
          />

          {/* Only show toggle buttons and JSON theme when type is "Partner" */}
          {isPartner && (
            <>
              {/* Enable Advisors Checkbox */}
              <ActiveInActiveButton
                mainText={"Enable Advisors"}
                selected={formik.values.enableAdvisors}
                onPress={() =>
                  formik.setFieldValue(
                    "enableAdvisors",
                    !formik.values.enableAdvisors
                  )
                }
                isWithWhiteBackground={true}
              />

              {/* Need Client ID Checkbox */}
              <ActiveInActiveButton
                mainText={"Need Client ID"}
                selected={formik.values.needClientId}
                onPress={() =>
                  formik.setFieldValue(
                    "needClientId",
                    !formik.values.needClientId
                  )
                }
                isWithWhiteBackground={true}
              />

              {/* Need Admin Dashboard Checkbox */}
              <ActiveInActiveButton
                isWithWhiteBackground={true}
                mainText={"Need Admin Dashboard"}
                selected={formik.values.needAdminDashboard}
                onPress={() => {
                  const newValue = !formik.values.needAdminDashboard;
                  formik.setFieldValue("needAdminDashboard", newValue);
                  if (!newValue) {
                    formik.setFieldValue("masterAdmin", "");
                    formik.setFieldValue("normalAdmins", []);
                  }
                }}
              />

              {/* Admin Section */}
              {formik.values.needAdminDashboard && (
                <>
                  <div style={styles.adminSectionContainer}>
                    <div style={{ flex: 1 }}>
                      <CustomInput
                        name="masterAdmin"
                        label="Master Admin Email"
                        type="email"
                        value={formik.values.masterAdmin}
                        onChange={formik.handleChange}
                        onBlure={formik.handleBlur}
                        placeholder="Enter master admin email"
                        error={
                          formik.errors.masterAdmin &&
                          formik.touched.masterAdmin
                            ? formik.errors.masterAdmin
                            : ""
                        }
                      />
                    </div>
                    <button
                      style={styles.addButton}
                      type="button"
                      title="Add normal admin"
                      onClick={() => {
                        const admins = [
                          ...(formik.values.normalAdmins || []),
                          "",
                        ];
                        formik.setFieldValue("normalAdmins", admins);
                      }}
                    >
                      +
                    </button>
                  </div>

                  {formik.values.normalAdmins &&
                    formik.values.normalAdmins.map((admin, index) => (
                      <div key={index} style={styles.adminInputContainer}>
                        <div style={styles.adminInputWrapper}>
                          <CustomInput
                            name={`normalAdmins[${index}]`}
                            label={`Admin ${index + 1} Email`}
                            type="email"
                            value={admin}
                            onChange={formik.handleChange}
                            placeholder="Enter admin email"
                            error={
                              formik.errors.normalAdmins?.[index]
                                ? String(formik.errors.normalAdmins[index])
                                : ""
                            }
                          />
                        </div>
                        <button
                          type="button"
                          title="Remove admin"
                          onClick={() => {
                            const admins = [...formik.values.normalAdmins];
                            admins.splice(index, 1);
                            formik.setFieldValue("normalAdmins", admins);
                          }}
                          style={styles.removeButton}
                        >
                          −
                        </button>
                      </div>
                    ))}
                </>
              )}
            </>
          )}

          {/* Submit Button */}
          {!createSuccess && (
             <CustomButton
            title={creating ? "Creating..." : "Create Business"}
            name="createBusiness"
            disabled={creating || !formik.isValid}
            handleClick={(event) => {
              event.preventDefault();
              formik.handleSubmit();
            }}
            loading={creating}
          />
          )}
          
          {/* {isPartner && (
           <div style={styles.jsonSection}>
                <label style={styles.jsonLabel}>
                  Paste JSON Theme (Client Code: {previewCode || "Enter name first"})
                </label>
                <textarea
                  style={{
                    ...styles.jsonTextarea,
                    borderColor: formik.errors.jsonTheme ? "#f44336" : "#ccc",
                  }}
                  name="jsonTheme"
                  value={formik.values.jsonTheme}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder='{"primaryColor": "#000000", "secondaryColor": "#ffffff", ...}'
                />
                {formik.errors.jsonTheme && formik.touched.jsonTheme && (
                  <span style={styles.jsonError}>{formik.errors.jsonTheme}</span>
                )}

               
                
                  <div style={styles.uploadButtonContainer}>
                  <button
                    type="button"
                    style={{
                      ...styles.uploadButton,
                      ...(uploading || !formik.values.jsonTheme || !formik.values.name
                        ? styles.uploadButtonDisabled
                        : {}),
                    }}
                    disabled={uploading || !formik.values.jsonTheme || !formik.values.name || !createSuccess }
                    onClick={handleUploadTheme}
                  >
                    {uploading ? "Uploading..." : "Upload Theme"}
                  </button>
                </div>
                
                
              </div>
          )} */}
        
        </form>
      </div>
    </div>
  );
};

export default WhiteLabelingForm;